import API from "@unisearch/helpers/api";
import { LayoutMain } from "@unisearch/theme/layout";
import { Fragment, useEffect, useState } from "react";
import dynamic from "next/dynamic";

import {
  StudyBanner,
  StudyBannerTwo,
  StudyLinks,
  StudyVideo,
} from "@modules/study/country/study_overview";
import { countryNameHelper, excerpt, getAssets } from "@unisearch/helpers";

const StudyOverviewAbout = dynamic(
  () => import("@modules/study/country/study_overview/study_overview_about"),
  {
    ssr: false,
  }
);
const StudyQuickFacts = dynamic(
  () => import("@modules/study/country/study_overview/study_quick_facts"),
  {
    ssr: false,
  }
);
const StudyStatictics = dynamic(
  () => import("@modules/study/country/study_overview/study_statictics"),
  {
    ssr: false,
  }
);
const StudySpecialization = dynamic(
  () => import("@modules/study/country/study_overview/study_specialization"),
  {
    ssr: false,
  }
);
const StudyOverviewTopRankTwo = dynamic(
  () => import("@modules/study/country/study_overview/study_top_rank_two"),
  {
    ssr: false,
  }
);
const StudyAdmissionRequirement = dynamic(
  () =>
    import("@modules/study/country/study_overview/study_admission_requirement"),
  {
    ssr: false,
  }
);
const StudyPostAdmission = dynamic(
  () => import("@modules/study/country/study_overview/study_post_admission"),
  {
    ssr: false,
  }
);
const FAQSection = dynamic(() => import("@modules/study/common/sections/faq"), {
  ssr: false,
});
const SectionLeadForm = dynamic(
  () => import("@modules/study/country/study_overview/section_lead_form"),
  {
    ssr: false,
  }
);
const ScrollPopup = dynamic(() => import("@unisearch/common/scroll_popup"), {
  ssr: false,
});

const Overview = ({ country_name, pageData, countryInfo }) => {
  const [formData, setFormData] = useState();
  const [faqData, setFaqData] = useState();

  useEffect(() => {
    const fetchData = async () => {
      const res = await API.get(`/public/lead-contact-form/pages/popup`);
      if (res && !res.error) {
        setFormData(res.data);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchFaqData = async () => {
      const countryId = countryInfo?.countryId;
      if (countryId) {
        const res = await API.get(`/public/country/faq/${countryId}`);
        if (res && !res.error) {
          setFaqData(res.data);
        }
      } else {
        setFaqData([]);
      }
    };
    fetchFaqData();
  }, [countryInfo?.countryId]);

  const JsonLd = () => {
    {
      return {
        __html: `
        {
          "@context": "https://schema.org",
          "@type": "Article",
          "mainEntityOfPage": {
               "@type": "WebPage",
               "@id": "${process.env.NEXT_PUBLIC_URL}/study/${country_name}"
          },
          "headline": "${pageData?.pageTitle}",
          "description":"${
            pageData?.pageContent ? excerpt(pageData?.pageContent, 500) : ""
          } | UniSearch",
          "image": "${pageData?.featureImage?.src ||
            getAssets("assets/images/study/country_placeholder.webp")}",
          "author": {
               "@type": "Organization",
               "name": "UniSearch"
          },
          "datePublished": "${pageData?.publishDate}"
     }
    `,
      };
    }
  };

  return (
    <Fragment>
      <LayoutMain
        isLeadPopup={true}
        footer_with_evaluation
        title={`Study in ${
          ["United States", "United Kingdom"].includes(countryInfo?.name)
            ? "the"
            : ""
        } ${countryInfo?.name}`}
        metaTitle={pageData?.metaTitle || pageData?.pageTitle}
        metaKeywords={pageData?.metaKeyword || "unisearch, study"}
        metaDescription={
          pageData?.metaDescription ||
          "Apply to Universities Abroad with expert guidance from existing students"
        }
        metaImage={pageData?.featureImage?.src}
        metaUrl={`${process.env.NEXT_PUBLIC_URL}/study/${country_name}`}
        JsonLd={JsonLd}
      >
        {pageData && pageData.videoType ? (
          <StudyBannerTwo
            pageData={pageData}
            name={country_name}
            linkType="study"
          />
        ) : (
          <StudyBanner
            pageData={pageData}
            name={country_name}
            linkType="study"
          />
        )}

        {pageData?.videoupload !== null ||
          (pageData?.embedCode !== "" && <StudyVideo pageData={pageData} />)}

        {pageData?.pageMenuGroup?.length > 0 && (
          <StudyLinks
            linkType="study"
            name={country_name}
            pageData={pageData}
          />
        )}
        <div className="overview_page_content">
          <StudyOverviewAbout
            countryInfo={countryInfo}
            country_slug={country_name}
            pageData={pageData}
          />

          <StudyQuickFacts countryInfo={countryInfo} pageData={pageData} />
          <StudyStatictics countryInfo={countryInfo} />
          <StudySpecialization />
          <StudyOverviewTopRankTwo
            heading={`Top Universities & Colleges in ${countryInfo?.name} Ranking, Fees & Requirement`}
          />
          <StudyAdmissionRequirement countryInfo={countryInfo} />
          <StudyPostAdmission countryInfo={countryInfo} />
          {faqData && faqData?.length > 0 && (
            <FAQSection
              title={`Frequently Asked Questions (${countryNameHelper(
                countryInfo
              )})`}
              faqData={faqData}
            />
          )}
        </div>
        {formData && <SectionLeadForm formData={formData} />}
      </LayoutMain>
      {/* <ScrollPopup /> */}
    </Fragment>
  );
};
export default Overview;

export async function getServerSideProps({ params }) {
  const { country_name } = params;

  let props = {
    countryInfo: {},
    pageData: {},
    country_name: country_name,
  };

  try {
    await API.get(`/public/page?pageType=country&slug=${country_name}`).then(
      (resp) => {
        if (!resp.error && resp.data) {
          props.pageData = resp.data;
        } else {
          return {
            notFound: true,
          };
        }
      }
    );

    await API.get(`/public/country/info/${country_name}`).then((resp) => {
      if (!resp.error && resp.data) {
        props.countryInfo = resp.data;
      }
    });

    return {
      props,
    };
  } catch (err) {
    return {
      props,
    };
  }
}
